import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { IoCopyOutline } from "react-icons/io5";

import { copy } from "../../utils/clipboard";
import { Button, IconButton } from "../Buttons";
import { ButtonWithConfirmDialogue } from "../ButtonWithConfirmDialogue";
import { ConfirmModal } from "../ConfirmModal";
import FileUploadInput from "../FileUploadInput/FileUploadInput";
import { Link } from "../Link";
import SettingsNote from "../Settings/SettingsNote";
import SettingsPageContainer from "../Settings/SettingsPageContainer";
import { successToast, useToast } from "../useToast";

interface FormValues {
  oktaDomain: string;
  metadataUrl: string;
}

interface SingleSignOnSetup {
  oktaDomain: string;
  metadataUrl: string;
  scimToken: string;
}

interface SingleSignOnSettingsFormProps {
  loading: boolean;
  setupLoading: boolean;
  disableLoading?: boolean;
  tokenLoading: boolean;
  uploadMetadataLoading?: boolean;
  data?: {
    singleSignOnSetup?: SingleSignOnSetup;
  };
  tokenData?: {
    generateToken?: {
      setup: SingleSignOnSetup;
    };
  };
  onSubmit: (values: FormValues) => void;
  onDisable?: () => void;
  onGenerateToken: () => void;
  onUploadMetadata?: (
    fileName: string,
    fileDataBase64: string
  ) => Promise<void>;
}

const SAML_METADATA_ALLOWED_FORMATS = {
  "application/xml": [".xml"],
  "text/xml": [".xml"],
};
const SAML_METADATA_ALLOWED_EXTENSIONS = Object.values(
  SAML_METADATA_ALLOWED_FORMATS
).flat();
const MAX_SAML_METADATA_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const getFileBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fileData = reader.result as string;
      const [, base64] = fileData.split(",");
      if (!base64) {
        reject(new Error("Failed to parse file"));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
  });
};

const SingleSignOnSettingsForm: React.FC<SingleSignOnSettingsFormProps> = ({
  loading,
  setupLoading,
  disableLoading,
  tokenLoading,
  uploadMetadataLoading,
  data,
  tokenData,
  onSubmit,
  onDisable,
  onGenerateToken,
  onUploadMetadata,
}) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMetadataFile, setSelectedMetadataFile] = useState<File | null>(
    null
  );
  const { register, handleSubmit, reset } = useForm<FormValues>({
    values: {
      oktaDomain: data?.singleSignOnSetup?.oktaDomain || "",
      metadataUrl: data?.singleSignOnSetup?.metadataUrl || "",
    },
  });

  const isLoading = loading || setupLoading || disableLoading;
  const scimToken =
    tokenData?.generateToken?.setup.scimToken ||
    data?.singleSignOnSetup?.scimToken;

  const isHashedToken =
    !!scimToken && scimToken === data?.singleSignOnSetup?.scimToken;

  const handleDisable = (): void => {
    onDisable?.();
    reset({
      oktaDomain: "",
      metadataUrl: "",
    });
  };

  const handleUploadMetadata = async (): Promise<void> => {
    if (!selectedMetadataFile || !onUploadMetadata) {
      return;
    }

    const hasValidMimeType =
      !selectedMetadataFile.type ||
      Object.keys(SAML_METADATA_ALLOWED_FORMATS).includes(
        selectedMetadataFile.type
      );
    const lowerCaseFileName = selectedMetadataFile.name.toLowerCase();
    const hasValidExtension = SAML_METADATA_ALLOWED_EXTENSIONS.some((ext) =>
      lowerCaseFileName.endsWith(ext)
    );
    if (!hasValidMimeType || !hasValidExtension) {
      toast({
        title: "Invalid file type",
        description: "Please upload an XML file.",
        status: "error",
        duration: 5000,
      });
      return;
    }

    if (selectedMetadataFile.size > MAX_SAML_METADATA_FILE_SIZE_BYTES) {
      toast({
        title: "Invalid file size",
        description: "File size must be less than 10 MB.",
        status: "error",
        duration: 5000,
      });
      return;
    }

    let fileDataBase64: string;
    try {
      fileDataBase64 = await getFileBase64(selectedMetadataFile);
    } catch {
      toast({
        title: "Error reading file",
        description: "Unable to process selected file. Please try again.",
        status: "error",
        duration: 5000,
      });
      return;
    }

    try {
      await onUploadMetadata(selectedMetadataFile.name, fileDataBase64);
      setSelectedMetadataFile(null);
    } catch {
      // Error toast is handled by parent mutation onError callback.
    }
  };

  return (
    <SettingsPageContainer heading="Single Sign On Setup">
      <Box my={8}>
        <SettingsNote mb="9">
          Creating users in Okta (with default settings) will provision a user
          account in BrightHire and email all users an invite to BrightHire.
          Most organizations DO NOT want invites to be sent when users are added
          before BrightHire training has occurred. Check with your internal
          project team or BrightHire CSM before creating users in Okta. More
          information on this setting can be found in Configuration Steps
          section of
          <Link
            href="https://help.brighthire.ai/en/articles/5870425-okta-scim-integration-guide"
            target="_blank"
          >
            {" this article."}
          </Link>
        </SettingsNote>
        <Heading as="h3" size="sm" mb={2}>
          Instructions
        </Heading>
        <Text maxW="500px" mb={2}>
          Please see the{" "}
          <Link
            target="_blank"
            href="https://help.brighthire.ai/en/articles/6501000-sso-setup"
          >
            help documentation
          </Link>
          .
        </Text>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing="8" align="start">
          <FormControl id="oktaDomain" isRequired maxW="400">
            <FormLabel>Unique Identifier or Okta Domain</FormLabel>
            <Input {...register("oktaDomain")} />
          </FormControl>
          <FormControl id="metadataUrl" isRequired maxW="400">
            <FormLabel>Metadata Url</FormLabel>
            <Input {...register("metadataUrl")} type="text" />
          </FormControl>
          {!!onUploadMetadata && (
            <FormControl id="metadataFileUpload">
              <FormLabel>SAML Metadata XML</FormLabel>
              <Box maxW="500">
                <FileUploadInput
                  value={selectedMetadataFile?.name}
                  allowedFormats={SAML_METADATA_ALLOWED_FORMATS}
                  showAllowedFormatsDescription={false}
                  onUpload={(files) => {
                    setSelectedMetadataFile(files[0] ?? null);
                  }}
                />
              </Box>
              <Flex alignItems="center" justifyContent="space-between" mt={1}>
                <Text color="gray.500" fontSize="sm">
                  Uploading a metadata file updates Metadata Url automatically.
                </Text>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleUploadMetadata}
                  isDisabled={!selectedMetadataFile || !!uploadMetadataLoading}
                  isLoading={uploadMetadataLoading}
                >
                  Upload
                </Button>
              </Flex>
            </FormControl>
          )}
          <FormControl id="scimToken" isRequired>
            <FormLabel>SCIM Token</FormLabel>
            <Flex gap="2" direction="column">
              {scimToken && (
                <InputGroup w={isHashedToken ? 400 : 710}>
                  <Input
                    name="scimToken"
                    type="text"
                    value={scimToken}
                    isReadOnly
                    _focus={{ borderColor: "inherit" }}
                    onClick={(e) => {
                      const el = e.target as HTMLInputElement;
                      el?.select?.();
                    }}
                  />
                  {!isHashedToken && (
                    <InputRightElement>
                      <Tooltip label="Copy SCIM token">
                        <IconButton
                          aria-label="Copy SCIM token"
                          variant="ghost"
                          boxSize="9"
                          mr="1"
                          icon={<IoCopyOutline />}
                          onClick={() => {
                            copy(scimToken);
                            successToast(toast, "SCIM token copied");
                          }}
                        />
                      </Tooltip>
                    </InputRightElement>
                  )}
                </InputGroup>
              )}
              <Button
                alignSelf="start"
                disabled={isLoading || tokenLoading}
                variant="danger"
                onClick={() => {
                  setIsOpen(true);
                }}
              >
                {scimToken ? "Regenerate" : "Generate"} SCIM token
              </Button>
              <ConfirmModal
                modalBodyText="This will regenerate your SCIM Token and you will need to update it in your SSO provider. Are you sure?"
                isOpen={isOpen}
                onCancel={() => {
                  setIsOpen(false);
                }}
                onConfirm={() => {
                  onGenerateToken();
                  setIsOpen(false);
                }}
              />
            </Flex>
          </FormControl>
          <HStack spacing={4}>
            <Button type="submit" disabled={isLoading}>
              Save Changes
            </Button>
            {!!onDisable && (
              <ButtonWithConfirmDialogue
                buttonProps={{ disabled: loading }}
                onConfirm={handleDisable}
                headerBody="This will disable your current SSO settings. Are you sure?"
              >
                Disable SSO
              </ButtonWithConfirmDialogue>
            )}
          </HStack>
        </VStack>
      </form>
    </SettingsPageContainer>
  );
};

export default SingleSignOnSettingsForm;
